import { Permission } from '@Constants/Permission.g';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { useHasPermission } from '@Contexts/appContext';
import { useBadges } from '@Hooks/useBadges';
import { ApiCostCenter } from '@Model';
import {
    Card,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumns,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useParams } from 'react-router';

export function FrameEditCostCenter(props: IFrameEditShellProps<ApiCostCenter>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiCostCenter>
            apiResourcePath={`${STAMMDATEN_API}/cost_centers`}
            renderer={FrameEditCostCenterBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.StammdatenCostCenterUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.StammdatenCostCenterCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.StammdatenCostCenterDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditCostCenterBody(props: IFrameBodyProps<ApiCostCenter>) {
    const i18n = useTranslate('');

    const binder = useGuiBinder<ApiCostCenter>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });
    const costCenterBadges = useBadges(props.data);

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Kostenstelle', props.mode),
                resourceLabel: 'Kostenstelle',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card
                name="PrimaryData"
                border={'withoutBorder'}
                label="Allgemeine Informationen"
                badges={costCenterBadges}
            >
                <Grid>
                    <GridColumns spans={[4, 4, 4, 12]}>
                        <TextInput {...binder.bindToProp<string>('label')} label={i18n('Bezeichnung')} />
                        <TextInput {...binder.bindToProp<string>('shorthand')} label={i18n('Suchcode')} />
                        <TextInput {...binder.bindToProp<string>('number')} label={i18n('Nummer')} />
                        <TextInput {...binder.bindToProp<string>('remarks')} inputSize={3} label={i18n('Bemerkung')} />
                    </GridColumns>
                </Grid>
            </Card>
        </EditFrameShell>
    );
}
